/* eslint-disable react-hooks/exhaustive-deps */
import {
  Add,
  Delete,
  ExitToApp,
  Info,
  Save,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  FormLabel,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ImagePicker } from "components";
import React, { useEffect, useState } from "react";
import { useForm, usePriorities, useSnackbar } from "hooks";
import DialogHeader from "./DialogHeader";
import ConfirmDialog from "./ConfirmDialog";
import { useBreakpoint, useTranslations } from "hooks";
import { create_UUID, getTranslation } from "common";
import { Validation, isRequired } from "hooks/useForm";
import { Priority } from "hooks/usePriorities";
import { Organization } from "model/organizationModel";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import OrganizationLabelsEditor from "components/OrganizationLabelsEditor";
import OrganizationCalendarSettings from "components/organization-calendar/OrganizationCalendarSettings";
interface OrganizationDialogProps {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
  onDelete?: () => void;
  onSave: (
    photo: string | null,
    name: string | null,
    priorities: Priority[]
  ) => Promise<void>;
  loading: boolean;
  onSavePriorities?: (priorities: Priority[]) => Promise<Priority[]>;
}

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
  open,
  organization,
  onClose,
  onSave,
  onDelete,
  loading,
  onSavePriorities,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [section, setSection] = useState("priorities");
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [exitFromOrganization, setExitFromOrganization] =
    useState<boolean>(false);
  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || {
        name: getTranslation(translations, "placeholders.nameisrequired.label"),
      },
  ];
  const [priorities, prioritiesLoading] = usePriorities(
    organization?.id || null
  );

  const isMobile = useBreakpoint("down", "sm");
  const { reset, isValid, changeHandler, values, errors, touched, setValues } =
    useForm(
      {
        priorities: [],
        name: organization?.name || "",
        icon: organization?.icon || "",
      },
      validations
    );

  useEffect(() => {
    if (!prioritiesLoading && priorities.length >= 0) {
      setValues((_: any) => ({ ..._, priorities }));
    }
  }, [prioritiesLoading, priorities]);

  useEffect(() => {
    if (!open) {
      reset();
      setExitFromOrganization(false);
      setSection("priorities");
    }
  }, [open]);

  useEffect(() => {
    if (organization) {
      setValues(organization);
    }
  }, [organization]);

  return (
    <>
    <Dialog
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => onClose()}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
        },
      }}
    >
      <DialogHeader
        onClose={onClose}
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          fontSize: 24,
          fontWeight: 800,
        }}
      >
        {organization?.id
          ? organization.name
          : getTranslation(translations, "organizations.edit.suggestnew")}
      </DialogHeader>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
          <Box data-testid="organization-image-settings" sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
              }}
            >
              <ImagePicker
                photoUrl={values?.icon || ""}
                relativePath={organization?.icon || ""}
                setPhotoUrl={(value) => changeHandler("icon", value)}
              />
            </Box>
            {onDelete && (
              <Box data-testid="organization-membership-actions" sx={{ mt: 2 }}>
                <FlowentiButton
                  id="btn-cancel-organization"
                  variant="contained"
                  color="error"
                  fullWidth
                  disabled={saveLoading || loading}
                  startIcon={<ExitToApp />}
                  size="small"
                  sx={{ borderColor: "error.main", "&:hover": { borderColor: "error.dark" } }}
                  onClick={() => setExitFromOrganization(true)}
                >
                  {getTranslation(translations, "generic.exit_organization.caption")}
                </FlowentiButton>
              </Box>
            )}
          </Box>
          <Grid flex={1} spacing={3} container data-testid="organization-identity-settings">
            <Grid item xs={12}>
              <TextField
                id="organizations-dialog"
                variant="outlined"
                fullWidth
                size="small"
                label={getTranslation(
                  translations,
                  "placeholders.organization.name.label"
                )}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                value={values?.name || ""}
                onChange={({ target }) => changeHandler("name", target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FlowentiButton
                id="btn-save-organization"
                variant="contained"
                disabled={!isValid || saveLoading || loading}
                startIcon={<Save />}
                size="small"
                onClick={async () => {
                  setSaveLoading(true);
                  try {
                    await onSave(values.icon, values.name, values.priorities);
                  } finally {
                    setSaveLoading(false);
                  }
                }}
              >
                {getTranslation(translations, "pm.button.save")}
              </FlowentiButton>
            </Grid>
          </Grid>
        </Stack>
        {organization?.id && (
          <Stack spacing={2} sx={{ width: "100%", mt: 3 }}>
            <Tabs
              value={section}
              onChange={(_, value) => setSection(value)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label={organization.name}
            >
              {[
                ["priorities", "generic.priorities.caption"],
                ["labels", "generic.labels.caption"],
                ["calendar", "calendar.settings.title"],
              ].map(([value, key]) => (
                <Tab
                  key={value}
                  value={value}
                  id={`organization-tab-${value}`}
                  aria-controls={`organization-panel-${value}`}
                  label={getTranslation(translations, key)}
                />
              ))}
            </Tabs>
            <Box role="tabpanel" id="organization-panel-priorities" aria-labelledby="organization-tab-priorities" hidden={section !== "priorities"}>
              <TableContainer
                component="fieldset"
                sx={{
                  borderRadius: 2,
                  borderColor: (_) => _.palette.divider,
                  borderWidth: "1px",
                  px: 3,
                  py: 2,
                }}
              >
                <FormLabel component="legend" sx={{ px: 1, fontWeight: 800, letterSpacing: 1 }}>
                  {getTranslation(translations, "generic.priorities.caption")}
                </FormLabel>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {getTranslation(
                          translations,
                          "register.registration.name.label"
                        )}
                      </TableCell>
                      <TableCell>
                        {getTranslation(translations, "generic.order.caption")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!prioritiesLoading &&
                      values &&
                      values.priorities &&
                      values.priorities
                        .filter((_: Priority) => !_.deleted)
                        .map(({ idorder, name, id, candelete }: Priority) => (
                          <TableRow key={`priority-${id}`}>
                            <TableCell sx={{ width: "48%" }}>
                              <TextField
                                id={`priorities-name-${id}`}
                                variant="standard"
                                size="small"
                                value={name}
                                onChange={({ target }) => {
                                  const idxToEdit = values.priorities.findIndex(
                                    (__: Priority) => __.id === id
                                  );
                                  const copyCurrentState = [
                                    ...values.priorities,
                                  ];
                                  copyCurrentState[idxToEdit].name =
                                    target.value;
                                  changeHandler("priorities", copyCurrentState);
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ width: "32%" }}>
                              <TextField
                                id={`priorities-order-${id}`}
                                variant="standard"
                                size="small"
                                value={idorder}
                                type="number"
                                inputProps={{
                                  min: 0,
                                }}
                                onChange={({ target }) => {
                                  const idxToEdit = values.priorities.findIndex(
                                    (__: Priority) => __.id === id
                                  );
                                  const copyCurrentState = [
                                    ...values.priorities,
                                  ];
                                  copyCurrentState[idxToEdit].idorder = Number(
                                    target.value
                                  );
                                  changeHandler("priorities", copyCurrentState);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  !candelete &&
                                  getTranslation(
                                    translations,
                                    "generic.cannot_delete_priority_assigned_to_activity.caption"
                                  )
                                }
                              >
                                <span>
                                  <IconButton
                                    disabled={!candelete}
                                    onClick={() => {
                                      const idxToDelete =
                                        values.priorities.findIndex(
                                          (__: Priority) => __.id === id
                                        );
                                      const copyCurrentState = [
                                        ...values.priorities,
                                      ];
                                      copyCurrentState[idxToDelete].deleted =
                                        true;
                                      changeHandler(
                                        "priorities",
                                        copyCurrentState
                                      );
                                    }}
                                    size="small"
                                  >
                                    {!candelete ? <Info /> : <Delete />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Stack justifyContent="center" alignItems="center">
                          <FlowentiButton
                            id="add-priorities"
                            onClick={() => {
                              changeHandler("priorities", [
                                ...values.priorities,
                                {
                                  id: create_UUID(),
                                  new: true,
                                  name: "",
                                  idorder: 0,
                                  candelete: true,
                                  idorganization: organization?.id || null,
                                },
                              ]);
                            }}
                            startIcon={<Add />}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                          >
                            {getTranslation(
                              translations,
                              "generic.add.caption"
                            )}
                          </FlowentiButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {onSavePriorities && (
                <Box sx={{ mt: 2, textAlign: "right" }}>
                  <FlowentiButton
                    id="btn-save-organization-priorities"
                    variant="contained"
                    startIcon={<Save />}
                    size="small"
                    disabled={prioritiesLoading || saveLoading || loading}
                    onClick={async () => {
                      setSaveLoading(true);
                      try {
                        changeHandler("priorities", await onSavePriorities(values.priorities));
                      } catch {
                        sendSnack({ message: getTranslation(translations, "generic.server.error"), type: "error" });
                      } finally {
                        setSaveLoading(false);
                      }
                    }}
                  >
                    {getTranslation(translations, "pm.button.save")}
                  </FlowentiButton>
                </Box>
              )}
            </Box>
            <Box role="tabpanel" id="organization-panel-labels" aria-labelledby="organization-tab-labels" hidden={section !== "labels"}>
              <Box
                component="fieldset"
                sx={{
                  borderRadius: 2,
                  borderColor: (_) => _.palette.divider,
                  borderWidth: "1px",
                  px: 3,
                  py: 2,
                }}
              >
                <FormLabel component="legend" sx={{ px: 1, fontWeight: 800, letterSpacing: 1 }}>
                  {getTranslation(translations, "generic.labels.caption")}
                </FormLabel>
                <OrganizationLabelsEditor organizationId={organization.id} />
              </Box>
            </Box>
            <Box role="tabpanel" id="organization-panel-calendar" aria-labelledby="organization-tab-calendar" hidden={section !== "calendar"}>
              <OrganizationCalendarSettings organizationId={organization.id} />
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
    <ConfirmDialog
      open={open && exitFromOrganization}
      onClose={() => setExitFromOrganization(false)}
      title={getTranslation(translations, "generic.exit_organization.caption")}
      content={getTranslation(translations, "pm.confirm.leave_organization")}
      cancelText={getTranslation(translations, "generic.button.cancel")}
      confirmText={getTranslation(translations, "generic.button.confirm")}
      onConfirm={() => {
        if (saveLoading || loading) return;
        setExitFromOrganization(false);
        onDelete?.();
      }}
    />
    </>
  );
};

export default OrganizationDialog;
